import { api } from './http';
import { definedFields } from './searchBody';
import type {
  CoClientMatter,
  Contact,
  ContactFilterOptions,
  ContactListResponse,
  DuplicateCheckResult,
} from './types';

export interface ContactListParams {
  search?: string;
  source?: string;
  state?: string;
  has_email?: boolean;
  has_phone?: boolean;
  created_from?: string;
  created_to?: string;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

/**
 * The contact list. Typed search text is a client's name, email or phone, so a
 * request that carries one goes to POST /contacts/search with the filters in the
 * JSON body: in a GET query string it would land in nginx access logs and Sentry
 * breadcrumbs (#2028). Filter-only requests keep the GET.
 */
export const getContacts = (params?: ContactListParams) => {
  if (params?.search) {
    return api.post<ContactListResponse>('/contacts/search', definedFields(params)).then(r => r.data);
  }
  return api.get<ContactListResponse>('/contacts', { params }).then(r => r.data);
};

export const getContactFilterOptions = () =>
  api.get<ContactFilterOptions>('/contacts/filter-options').then(r => r.data);

export const getContact = (id: number) =>
  api.get<Contact>(`/contacts/${id}`).then(r => r.data);

export const createContact = (data: Partial<Contact>) =>
  api.post<Contact>('/contacts', data).then(r => r.data);

export const updateContact = (id: number, data: Partial<Contact>) =>
  api.patch<Contact>(`/contacts/${id}`, data).then(r => r.data);

/** The new client's name, email and phone travel in the POST body, never the URL (#2028). */
export const checkContactDuplicates = (params: { first_name: string; last_name: string; email?: string; phone?: string }) =>
  api.post<DuplicateCheckResult>('/contacts/duplicate-check', definedFields(params)).then(r => r.data);

export const getContactCoClientMatters = (contactId: number) =>
  api.get<CoClientMatter[]>(`/contacts/${contactId}/co-client-matters`).then(r => r.data);

export const setContactSsn = (id: number, ssn: string) =>
  api.put<Contact>(`/contacts/${id}/ssn`, { ssn }).then(r => r.data);

export const clearContactSsn = (id: number) =>
  api.delete<Contact>(`/contacts/${id}/ssn`).then(r => r.data);

export const revealContactSsn = (id: number) =>
  api.post<{ ssn: string | null }>(`/contacts/${id}/ssn/reveal`).then(r => r.data);
